import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef
} from 'react'

import { ZIndex } from 'shared/ui/types'
import { lockBodyScroll, unlockBodyScroll } from 'shared/ui/utils/layout'

export type OverlayType =
  | 'modal'
  | 'drawer'
  | 'tooltip'
  | 'dropdown'
  | 'popover'

const mapTypeToZIndex = {
  modal: ZIndex.MODAL,
  drawer: ZIndex.DRAWER,
  tooltip: ZIndex.TOOLTIP,
  dropdown: ZIndex.DROPDOWN,
  popover: ZIndex.POPOVER
}

// Types of overlays that should lock the body scroll
const SCROLL_LOCKING_TYPES: OverlayType[] = ['modal', 'drawer']

export interface OverlayEntryPayload {
  id: string
  type: OverlayType
}

interface OverlayEntry extends OverlayEntryPayload {
  zIndex: number
}
interface OverlayState {
  stack: OverlayEntry[]
}

type OverlayAction =
  | { type: 'REGISTER'; entry: OverlayEntryPayload }
  | { type: 'UNREGISTER'; id: string }

const overlayActions = (
  state: OverlayState,
  action: OverlayAction
): OverlayState => {
  const { stack } = state

  switch (action.type) {
    case 'REGISTER': {
      const lastEntry = stack[stack.length - 1]
      const newZIndex = lastEntry
        ? lastEntry.zIndex + 1
        : mapTypeToZIndex[action.entry.type]

      return {
        stack: [
          ...stack.filter(entry => entry.id !== action.entry.id),
          { ...action.entry, zIndex: newZIndex }
        ]
      }
    }

    case 'UNREGISTER': // Remove the entry with the specified id
      return {
        stack: stack.filter(entry => entry.id !== action.id)
      }

    default:
      return state
  }
}

interface OverlayStackContextValue {
  stack: OverlayEntry[]
  register: (entry: OverlayEntryPayload) => void
  unregister: (id: string) => void
  isTop: (id: string) => boolean
  getZIndex: (id: string) => number | undefined
}

const OverlayStackContext = createContext<OverlayStackContextValue | null>(null)

export const OverlayStackProvider = ({ children }: { children: ReactNode }) => {
  const [{ stack }, dispatch] = useReducer(overlayActions, { stack: [] })

  const originalBodyStyles = useRef<{
    overflow?: string
    paddingRight?: string
  } | null>(null)

  const register = useCallback(
    (entry: OverlayEntryPayload) => dispatch({ type: 'REGISTER', entry }),
    []
  )

  const unregister = useCallback(
    (id: string) => dispatch({ type: 'UNREGISTER', id }),
    []
  )

  const isTop = useCallback(
    (id: string) => stack[stack.length - 1]?.id === id,
    [stack]
  )

  const getZIndex = useCallback(
    (id: string) => stack.find(entry => entry.id === id)?.zIndex,
    [stack]
  )

  // Scroll lock side-effect
  useEffect(() => {
    const lockingOverlays = stack.filter(entry =>
      SCROLL_LOCKING_TYPES.includes(entry.type)
    )

    if (lockingOverlays.length > 0 && !originalBodyStyles.current) {
      const body = document.body

      originalBodyStyles.current = {
        overflow: body.style.overflow,
        paddingRight: body.style.paddingRight
      }

      lockBodyScroll()
    }

    if (lockingOverlays.length === 0 && originalBodyStyles.current) {
      unlockBodyScroll(originalBodyStyles.current)
      originalBodyStyles.current = null
    }
  }, [stack])

  return (
    <OverlayStackContext.Provider
      value={{
        stack: stack,
        register,
        unregister,
        isTop,
        getZIndex
      }}
    >
      {children}
    </OverlayStackContext.Provider>
  )
}

export const useOverlayStack = () => {
  const ctx = useContext(OverlayStackContext)

  if (!ctx) throw new Error('OverlayStackProvider missing')

  return ctx
}
